import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Button,
  Typography,
  IconButton,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { TipoMaterialN1, TIPO_MATERIAL_LABELS, ITipoMaterialPermitido, IHistoricoImportacao } from "../../types/materiaisN1.types";
import { useNavigation } from "contexts/notificationsContext";
import { AbaImportarMidias } from "./AbaImportarMidias";
import { useTravaPlanilhaN1 } from "../../hooks/useTravaPlanilhaN1";

interface ModalImportacaoProps {
  open: boolean;
  onClose: () => void;
  tiposPermitidos: ITipoMaterialPermitido[];
  tipoMaterialSelecionado: TipoMaterialN1 | null;
  onImportar: (tipoMaterial: TipoMaterialN1, arquivo: File) => Promise<void>;
  onBaixarModelo: (tipoMaterial: TipoMaterialN1) => Promise<void>;
  onRemoverUltimaImportacao?: (idImportacao: number) => Promise<void>;
  historicoImportacoes?: IHistoricoImportacao[];
  ultimaImportacao?: IHistoricoImportacao | null;
  isLoading?: boolean;
}

export const ModalImportacao: React.FC<ModalImportacaoProps> = ({
  open,
  onClose,
  tiposPermitidos,
  tipoMaterialSelecionado,
  onImportar,
  onBaixarModelo,
  onRemoverUltimaImportacao,
  ultimaImportacao,
}) => {
  const { openNotification } = useNavigation();
  const [tabValue, setTabValue] = useState<"importar" | "midias" | "historico">("importar");
  const [arquivoSelecionado, setArquivoSelecionado] = useState<File | null>(null);
  const [tipoSelecionado, setTipoSelecionado] = useState<TipoMaterialN1>(
    tipoMaterialSelecionado || tiposPermitidos[0]?.tipoMaterial || "perfis"
  );
  // FASE 05 — 3ª validação/PO 2026-07-28 (Frentes 2 e 12): estados de processo em andamento
  // para a importação e para a exclusão da última planilha (antes eram silenciosos).
  const [isImportando, setIsImportando] = useState(false);
  const [isRemovendo, setIsRemovendo] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Plano 14/10/2026 (Frente 4): exclusão mútua planilha × edição manual do tipo escolhido no
  // modal — edição manual pendente bloqueia a planilha; planilha pendente bloqueia as mídias.
  const { trava, recarregar: recarregarTrava } = useTravaPlanilhaN1(open ? tipoSelecionado : null);
  useEffect(() => {
    if (open) recarregarTrava();
  }, [open, ultimaImportacao, recarregarTrava]);
  const importacaoBloqueada = !!trava?.edicaoManualPendente;

  // Resetar ao abrir/fechar modal
  useEffect(() => {
    if (open) {
      setTabValue("importar");
      setArquivoSelecionado(null);
      setTipoSelecionado(tipoMaterialSelecionado || tiposPermitidos[0]?.tipoMaterial || "perfis");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [open, tipoMaterialSelecionado, tiposPermitidos]);

  const handleSelecionarArquivo = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validar extensão
      const extensoesPermitidas = [".xlsx", ".xls"];
      const extensao = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
      if (!extensoesPermitidas.includes(extensao)) {
        openNotification({ message: "Arquivo inválido. Selecione um arquivo .xlsx ou .xls", type: "error" });
        return;
      }
      // Validar tamanho (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        openNotification({ message: "Arquivo muito grande. Tamanho máximo: 10MB", type: "error" });
        return;
      }
      setArquivoSelecionado(file);
    }
    e.target.value = "";
  };

  const handleBaixarModelo = async () => {
    try {
      await onBaixarModelo(tipoSelecionado);
      openNotification({ message: "Modelo baixado com sucesso", type: "success" });
    } catch (error: any) {
      // FASE 05 — 3ª validação/PO 2026-07-28 (Frente 11): a falha deixou de ser engolida —
      // antes o botão "não fazia nada" e ainda aparecia toast de sucesso.
      console.error("Erro ao baixar modelo:", error);
      openNotification({
        message: "Não foi possível baixar a planilha padrão. Tente novamente e, se persistir, informe o suporte.",
        type: "error",
      });
    }
  };

  // FASE 05 — 3ª validação/PO 2026-07-28 (Frente 2): botão que efetivamente dispara a
  // importação do arquivo selecionado (antes o onImportar nunca era chamado no modal).
  const handleImportarArquivo = async () => {
    if (!arquivoSelecionado || isImportando || importacaoBloqueada) return;
    setIsImportando(true);
    try {
      await onImportar(tipoSelecionado, arquivoSelecionado);
      setArquivoSelecionado(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Erro ao importar planilha:", error);
      openNotification({ message: "Erro ao importar a planilha. Tente novamente.", type: "error" });
    } finally {
      setIsImportando(false);
    }
  };

  const handleRemoverUltimaImportacao = async () => {
    if (!ultimaImportacao || !onRemoverUltimaImportacao || isRemovendo) return;

    if (!window.confirm("Tem certeza que deseja remover a última importação? Esta ação não poderá ser desfeita.")) {
      return;
    }

    // FASE 05 — 3ª validação/PO 2026-07-28 (Frente 12): a exclusão desfaz os efeitos da
    // planilha (rollback) e pode demorar — o usuário passa a ver o processo em andamento.
    // O resultado (sucesso/erro) é notificado pelo chamador, que também atualiza a tela.
    setIsRemovendo(true);
    try {
      await onRemoverUltimaImportacao(ultimaImportacao.idImportacao);
    } catch (error) {
      console.error("Erro ao remover importação:", error);
      openNotification({ message: "Erro ao remover a última importação. Tente novamente.", type: "error" });
    } finally {
      setIsRemovendo(false);
    }
  };

  // Filtrar apenas tipos com permissão de importação
  const tiposComPermissao = tiposPermitidos.filter(t => t.podeImportar);

  // Verificar se pode remover última importação
  const podeRemoverUltimaImportacao =
    ultimaImportacao &&
    ultimaImportacao.situacao !== "disponivel_mercado" &&
    onRemoverUltimaImportacao;

  // Labels de situação
  const getSituacaoLabel = (situacao: string) => {
    const labels: Record<string, string> = {
      em_edicao: "Em edição",
      em_homologacao: "Em homologação",
      homologado: "Homologado",
      disponivel_mercado: "Disponível mercado",
    };
    return labels[situacao] || situacao;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxWidth: "700px",
          borderRadius: 2,
        },
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 3, pt: 2, pb: 1 }}>
        <Typography variant="h6" sx={{ color: "#444", fontWeight: 500 }}>
          Importação de cadastros
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ pt: 1 }}>
        {/* Radio Buttons - Tipo de Produto */}
        <RadioGroup
          row
          value={tipoSelecionado}
          onChange={(e) => setTipoSelecionado(e.target.value as TipoMaterialN1)}
          sx={{ mb: 2, flexWrap: "nowrap" }}
        >
          {tiposComPermissao.map((tipo) => (
            <FormControlLabel
              key={tipo.idTipoMaterial}
              value={tipo.tipoMaterial}
              control={<Radio size="small" sx={{ color: "#3C90AD", "&.Mui-checked": { color: "#3C90AD" } }} />}
              label={
                <Typography sx={{ fontSize: "0.75rem", whiteSpace: "nowrap" }}>
                  {TIPO_MATERIAL_LABELS[tipo.tipoMaterial]}
                </Typography>
              }
              sx={{ mr: 2 }}
            />
          ))}
        </RadioGroup>

        <Divider sx={{ mb: 2 }} />

        {/* Botões de Abas (sólidos) */}
        <Box sx={{ display: "flex", mb: 3 }}>
          <Button
            variant="contained"
            onClick={() => setTabValue("importar")}
            sx={{
              backgroundColor: tabValue === "importar" ? "#3C90AD" : "#75B0C7",
              color: "#fff",
              borderRadius: 0,
              px: 4,
              py: 1.5,
              fontWeight: 500,
              fontSize: "0.85rem",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: tabValue === "importar" ? "#2d6f87" : "#5a9ab5",
                boxShadow: "none",
              },
            }}
          >
            IMPORTAR CADASTROS
          </Button>
          {/* FASE 05 — Ajustes 25/08 (Etapa E — item 13 do docx de 22/08): aba nova, entre
              IMPORTAR CADASTROS e HISTÓRICO, para a carga em massa de mídias por .ZIP. */}
          <Button
            variant="contained"
            onClick={() => setTabValue("midias")}
            sx={{
              backgroundColor: tabValue === "midias" ? "#3C90AD" : "#75B0C7",
              color: "#fff",
              borderRadius: 0,
              px: 4,
              py: 1.5,
              fontWeight: 500,
              fontSize: "0.85rem",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: tabValue === "midias" ? "#2d6f87" : "#5a9ab5",
                boxShadow: "none",
              },
            }}
          >
            IMPORTAR MÍDIAS
          </Button>
          <Button
            variant="contained"
            onClick={() => setTabValue("historico")}
            sx={{
              backgroundColor: tabValue === "historico" ? "#3C90AD" : "#75B0C7",
              color: "#fff",
              borderRadius: 0,
              px: 4,
              py: 1.5,
              fontWeight: 500,
              fontSize: "0.85rem",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: tabValue === "historico" ? "#2d6f87" : "#5a9ab5",
                boxShadow: "none",
              },
            }}
          >
            HISTÓRICO DE IMPORTAÇÃO
          </Button>
        </Box>

        {/* Conteúdo da aba IMPORTAR CADASTROS */}
        {tabValue === "importar" && (
          <Box>
            {/* Botão Baixar Planilha Padrão */}
            <Button
              variant="contained"
              onClick={handleBaixarModelo}
              sx={{
                backgroundColor: "#3C90AD",
                color: "#fff",
                borderRadius: 0,
                px: 3,
                py: 1,
                fontWeight: 500,
                fontSize: "0.8rem",
                boxShadow: "none",
                mb: 2,
                "&:hover": {
                  backgroundColor: "#2d6f87",
                  boxShadow: "none",
                },
              }}
            >
              BAIXAR PLANILHA PADRÃO
            </Button>

            {/* Campo de Upload */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <Box
              sx={{
                display: "flex",
                border: "1px solid #ccc",
                borderRadius: 0,
                overflow: "hidden",
                mb: 3,
              }}
            >
              <Button
                onClick={handleSelecionarArquivo}
                sx={{
                  backgroundColor: "#75B0C7",
                  color: "#fff",
                  borderRadius: 0,
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  minWidth: 120,
                  "&:hover": {
                    backgroundColor: "#5a9ab5",
                  },
                }}
              >
                BUSCAR
              </Button>
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  px: 2,
                  backgroundColor: "#fff",
                  borderLeft: "1px solid #ccc",
                }}
              >
                <Typography
                  sx={{
                    color: "#3C90AD",
                    fontSize: "0.85rem",
                    fontWeight: 500,
                  }}
                >
                  {arquivoSelecionado ? arquivoSelecionado.name.toUpperCase() : ""}
                </Typography>
              </Box>
            </Box>

            {importacaoBloqueada && (
              <Alert severity="warning" sx={{ mb: 2, fontSize: "0.85rem" }}>
                {trava?.mensagemEdicaoManualPendente}
              </Alert>
            )}

            {/* FASE 05 — 3ª validação/PO 2026-07-28 (Frente 2): botão que dispara a importação */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
              <Button
                variant="contained"
                onClick={handleImportarArquivo}
                disabled={!arquivoSelecionado || isImportando || importacaoBloqueada}
                sx={{
                  backgroundColor: "#3C90AD",
                  color: "#fff",
                  borderRadius: 0,
                  px: 4,
                  py: 1,
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#2d6f87",
                    boxShadow: "none",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#c5c5c5",
                    color: "#fff",
                  },
                }}
              >
                {isImportando ? "IMPORTANDO..." : "IMPORTAR"}
              </Button>
              {isImportando && <CircularProgress size={22} sx={{ color: "#3C90AD" }} />}
              {!arquivoSelecionado && !isImportando && (
                <Typography sx={{ color: "#999", fontSize: "0.8rem", fontStyle: "italic" }}>
                  Selecione uma planilha (.xlsx ou .xls) para habilitar a importação
                </Typography>
              )}
            </Box>

            {/* Última planilha importada */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <Typography sx={{ color: "#666", fontSize: "0.85rem", whiteSpace: "nowrap" }}>
                Última planilha importada
              </Typography>
              <Divider sx={{ flex: 1 }} />
            </Box>

            {ultimaImportacao ? (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography sx={{ color: "#333", fontSize: "0.85rem" }}>
                  {new Date(ultimaImportacao.dataHoraImportacao).toLocaleString("pt-BR", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })}
                </Typography>
                <Typography sx={{ color: "#333", fontSize: "0.85rem" }}>
                  {getSituacaoLabel(ultimaImportacao.situacao)}
                </Typography>
                {isRemovendo ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={18} sx={{ color: "#d32f2f" }} />
                    <Typography sx={{ color: "#666", fontSize: "0.8rem", fontStyle: "italic" }}>
                      Excluindo importação e desfazendo os efeitos...
                    </Typography>
                  </Box>
                ) : (
                  podeRemoverUltimaImportacao && (
                    <IconButton
                      onClick={handleRemoverUltimaImportacao}
                      sx={{ color: "#d32f2f" }}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  )
                )}
              </Box>
            ) : (
              <Typography sx={{ color: "#999", fontSize: "0.85rem", fontStyle: "italic" }}>
                Nenhuma importação realizada
              </Typography>
            )}
          </Box>
        )}

        {/* Conteúdo da aba IMPORTAR MÍDIAS (Etapa E — item 13 do docx de 22/08) */}
        {tabValue === "midias" && (
          trava?.planilhaPendente ? (
            <Alert severity="warning" sx={{ mt: 1, fontSize: "0.85rem" }}>
              {trava.mensagemPlanilhaPendente}
            </Alert>
          ) : (
            <AbaImportarMidias
              tipoMaterial={tipoSelecionado}
              onImportado={onClose}
            />
          )
        )}

        {/* Conteúdo da aba HISTÓRICO DE IMPORTAÇÃO */}
        {tabValue === "historico" && (
          <Box>
            <Typography sx={{ color: "#999", fontSize: "0.85rem", textAlign: "center", py: 4 }}>
              Nenhum histórico de importação encontrado.
            </Typography>
          </Box>
        )}
      </DialogContent>

    </Dialog>
  );
};
